/**
 * Candidates Page Component
 * Displays candidate analytics overview with charts, leaderboard, and activity feed
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Clock, Trophy, TrendingUp, Target, Activity, CheckCircle2, ArrowRight, Calendar, Mail, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { AppHeader } from './AppHeader';
import { API_BASE, getAuthHeaders } from '../../utils/api';

interface TopPerformer {
    id: string;
    user_id?: string;
    name: string;
    email?: string;
    image_url?: string;
    score: number;
    simulation_title: string;
}

interface RecentActivity {
    id: string;
    user_id?: string;
    name: string;
    image_url?: string;
    simulation_title: string;
    score: number | null;
    completed_at: string;
    status: string;
}

interface Analytics {
    total_candidates: number;
    status_breakdown: {
        completed: number;
        in_progress: number;
        not_started: number;
    };
    total_sessions: number;
    average_score: number | null;
    score_percentiles: {
        p25?: number;
        p50?: number;
        p75?: number;
        p90?: number;
    };
    average_duration_minutes: number | null;
    total_time_minutes: number;
    score_distribution: { range: string; count: number }[];
    candidates_by_simulation: { simulation_title: string; simulation_id: string; count: number }[];
    top_performers: TopPerformer[];
    recent_activity: RecentActivity[];
}

interface InvitedCandidate {
    id: string;
    invitation_id: string;
    email: string;
    name: string;
    simulation_id: string;
    simulation_title: string;
    status: 'scheduled' | 'in_progress' | 'completed' | 'expired' | 'cancelled';
    invitation_status: string;
    session_id?: string;
    invited_at: string;
    accepted_at?: string;
    invited_by?: string;
    expires_at: string;
    score?: number | null;
    is_external_candidate: boolean;
}

interface InvitedCandidatesStats {
    total: number;
    scheduled: number;
    in_progress: number;
    completed: number;
}

interface CandidatesPageProps {
    // Props are now optional since navigation is handled by router
}

function AnalyticsCardSkeleton() {
    return (
        <div className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
            <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-slate-200 rounded-lg"></div>
                <div className="space-y-2">
                    <div className="h-3 w-24 bg-slate-200 rounded"></div>
                    <div className="h-7 w-16 bg-slate-200 rounded"></div>
                </div>
            </div>
            <div className="mt-3 flex space-x-3">
                <div className="h-3 w-20 bg-slate-200 rounded"></div>
                <div className="h-3 w-20 bg-slate-200 rounded"></div>
            </div>
        </div>
    );
}

function ChartSkeleton() {
    return (
        <div className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
            <div className="h-5 w-40 bg-slate-200 rounded mb-4"></div>
            <div className="h-[250px] bg-slate-100 rounded flex items-center justify-center">
                <div className="text-slate-400 text-sm">Loading chart...</div>
            </div>
        </div>
    );
}

function LeaderboardSkeleton() {
    return (
        <div className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
            <div className="flex items-center justify-between mb-4">
                <div className="h-5 w-32 bg-slate-200 rounded"></div>
                <div className="w-5 h-5 bg-slate-200 rounded"></div>
            </div>
            <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="flex items-center gap-3 p-3">
                        <div className="w-8 h-8 bg-slate-200 rounded-full"></div>
                        <div className="w-10 h-10 bg-slate-200 rounded-full"></div>
                        <div className="flex-1 space-y-2">
                            <div className="h-4 w-24 bg-slate-200 rounded"></div>
                            <div className="h-3 w-32 bg-slate-200 rounded"></div>
                        </div>
                        <div className="h-6 w-10 bg-slate-200 rounded"></div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ActivitySkeleton() {
    return (
        <div className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
            <div className="flex items-center justify-between mb-4">
                <div className="h-5 w-32 bg-slate-200 rounded"></div>
                <div className="w-5 h-5 bg-slate-200 rounded"></div>
            </div>
            <div className="space-y-3">
                {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="flex items-start gap-3 p-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-full"></div>
                        <div className="flex-1 space-y-2">
                            <div className="h-4 w-24 bg-slate-200 rounded"></div>
                            <div className="h-3 w-40 bg-slate-200 rounded"></div>
                            <div className="flex gap-2">
                                <div className="h-5 w-16 bg-slate-200 rounded-full"></div>
                                <div className="h-5 w-14 bg-slate-200 rounded"></div>
                            </div>
                        </div>
                        <div className="h-3 w-16 bg-slate-200 rounded"></div>
                    </div>
                ))}
            </div>
        </div>
    );
}

const CHART_COLORS = {
    primary: '#10b981',
    secondary: '#3b82f6',
    tertiary: '#8b5cf6'
};

export function CandidatesPage({}: CandidatesPageProps) {
    const navigate = useNavigate();
    const [analytics, setAnalytics] = useState<Analytics | null>(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [invitedCandidates, setInvitedCandidates] = useState<InvitedCandidate[]>([]);
    const [invitedStats, setInvitedStats] = useState<InvitedCandidatesStats | null>(null);
    const [invitedLoading, setInvitedLoading] = useState(true);

    useEffect(() => {
        loadAnalytics();
        loadInvitedCandidates();
    }, []);

    const loadAnalytics = async () => {
        setAnalyticsLoading(true);
        try {
            const authHeaders = await getAuthHeaders();
            const response = await axios.get(`${API_BASE}/candidates/analytics`, { headers: authHeaders });
            setAnalytics(response.data.data);
        } catch (err) {
            console.error('Failed to load analytics:', err);
            setError('Failed to load analytics data');
        } finally {
            setAnalyticsLoading(false);
        }
    };

    const loadInvitedCandidates = async () => {
        setInvitedLoading(true);
        try {
            const authHeaders = await getAuthHeaders();
            const response = await axios.get(`${API_BASE}/invitations/dashboard`, { headers: authHeaders });
            const data = response.data.data;
            setInvitedCandidates(data.candidates || []);
            setInvitedStats(data.stats || null);
        } catch (err) {
            console.error('Failed to load invited candidates:', err);
        } finally {
            setInvitedLoading(false);
        }
    };

    const statusPieData = useMemo(() => {
        if (!analytics) return [];
        return [
            { name: 'Completed', value: analytics.status_breakdown.completed, color: '#10b981' },
            { name: 'In Progress', value: analytics.status_breakdown.in_progress, color: '#3b82f6' },
            { name: 'Not Started', value: analytics.status_breakdown.not_started, color: '#94a3b8' },
        ].filter(d => d.value > 0);
    }, [analytics]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-700 border-green-200';
            case 'in_progress': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
            case 'not_started': return 'bg-slate-100 text-slate-600 border-slate-200';
            case 'scheduled': return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'expired': return 'bg-red-100 text-red-600 border-red-200';
            case 'cancelled': return 'bg-slate-100 text-slate-500 border-slate-200';
            default: return 'bg-slate-100 text-slate-600 border-slate-200';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'scheduled': return 'Scheduled';
            case 'in_progress': return 'In Progress';
            case 'completed': return 'Completed';
            case 'expired': return 'Expired';
            case 'cancelled': return 'Cancelled';
            default: return status;
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getInitials = (name: string) => {
        const parts = name.split(' ').filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    const getAvatarColor = (name: string) => {
        const colors = [
            'bg-blue-500', 'bg-emerald-500', 'bg-purple-500',
            'bg-orange-500', 'bg-pink-500', 'bg-cyan-500'
        ];
        const index = name.charCodeAt(0) % colors.length;
        return colors[index];
    };

    const getScoreColor = (score: number) => {
        if (score >= 90) return 'text-green-600';
        if (score >= 80) return 'text-emerald-600';
        if (score >= 70) return 'text-yellow-600';
        if (score >= 60) return 'text-orange-600';
        return 'text-red-600';
    };

    const completionRate = analytics && analytics.total_candidates > 0
        ? (analytics.status_breakdown.completed / analytics.total_candidates) * 100
        : 0;

    if (analyticsLoading) {
        return (
            <div className="min-h-screen bg-slate-50">
                <AppHeader activePage="candidates" />
                <div className="flex items-center justify-center h-[calc(100vh-64px)]">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-slate-600">Loading analytics...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <AppHeader activePage="candidates" />

            <main className="max-w-7xl mx-auto px-6 py-8">
                <div className="space-y-6">
                    {/* Page Header */}
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-900 mb-2">Candidates Overview</h1>
                            <p className="text-slate-600">
                                Monitor all candidates and simulation performance at a glance
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/app/results')}
                            className="flex items-center space-x-2 px-4 py-2 bg-emerald-500 text-white font-medium rounded-lg hover:bg-emerald-600 transition-colors"
                        >
                            <span>View All Results</span>
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    {/* Analytics Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {analyticsLoading ? (
                            <>
                                <AnalyticsCardSkeleton />
                                <AnalyticsCardSkeleton />
                                <AnalyticsCardSkeleton />
                                <AnalyticsCardSkeleton />
                            </>
                        ) : analytics ? (
                            <>
                                <div className="bg-white rounded-xl border border-slate-200 p-5">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                            <Users className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-500">Total Candidates</p>
                                            <p className="text-2xl font-bold text-slate-900">{analytics.total_candidates}</p>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex space-x-3 text-xs">
                                        <span className="text-green-600">{analytics.status_breakdown.completed} completed</span>
                                        <span className="text-yellow-600">{analytics.status_breakdown.in_progress} in progress</span>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl border border-slate-200 p-5">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                                            <Trophy className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-500">Average Score</p>
                                            <p className="text-2xl font-bold text-slate-900">
                                                {analytics.average_score !== null ? `${analytics.average_score.toFixed(0)}` : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    {analytics.score_percentiles.p50 !== undefined ? (
                                        <div className="mt-3 text-xs text-slate-500">
                                            Median: {analytics.score_percentiles.p50.toFixed(0)}
                                        </div>
                                    ) : (
                                        <div className="mt-3 text-xs text-slate-400">No score data yet</div>
                                    )}
                                </div>

                                <div className="bg-white rounded-xl border border-slate-200 p-5">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-500">Completion Rate</p>
                                            <p className="text-2xl font-bold text-slate-900">
                                                {completionRate.toFixed(0)}%
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-3 text-xs text-slate-500">
                                        {analytics.status_breakdown.completed} of {analytics.total_candidates} completed
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl border border-slate-200 p-5">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                            <TrendingUp className="w-5 h-5 text-orange-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-500">Total Sessions</p>
                                            <p className="text-2xl font-bold text-slate-900">{analytics.total_sessions}</p>
                                        </div>
                                    </div>
                                    <div className="mt-3 text-xs text-slate-500">
                                        {analytics.total_candidates > 0
                                            ? `${(analytics.total_sessions / analytics.total_candidates).toFixed(1)} per candidate`
                                            : 'No data'}
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {analyticsLoading ? (
                            <>
                                <ChartSkeleton />
                                <ChartSkeleton />
                            </>
                        ) : analytics ? (
                            <>
                                {/* Status Pie Chart */}
                                <div className="bg-white rounded-xl border border-slate-200 p-6">
                                    <h3 className="text-lg font-bold text-slate-900 mb-4">Candidates by Status</h3>
                                    {statusPieData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={250}>
                                            <PieChart>
                                                <Pie
                                                    data={statusPieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                                    outerRadius={80}
                                                    fill="#8884d8"
                                                    dataKey="value"
                                                >
                                                    {statusPieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-[250px] flex items-center justify-center text-slate-400">
                                            No data available
                                        </div>
                                    )}
                                </div>

                                {/* Score Distribution Bar Chart */}
                                <div className="bg-white rounded-xl border border-slate-200 p-6">
                                    <h3 className="text-lg font-bold text-slate-900 mb-4">Score Distribution</h3>
                                    {analytics.score_distribution && analytics.score_distribution.some(d => d.count > 0) ? (
                                        <ResponsiveContainer width="100%" height={250}>
                                            <BarChart data={analytics.score_distribution}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis dataKey="range" tick={{ fill: '#64748b', fontSize: 12 }} />
                                                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} allowDecimals={false} />
                                                <Tooltip />
                                                <Bar dataKey="count" fill={CHART_COLORS.primary} radius={[8, 8, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-[250px] flex items-center justify-center text-slate-400">
                                            No score data available
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : null}
                    </div>

                    {/* Candidates by Simulation */}
                    {analyticsLoading ? (
                        <ChartSkeleton />
                    ) : analytics && analytics.candidates_by_simulation && analytics.candidates_by_simulation.length > 0 ? (
                        <div className="bg-white rounded-xl border border-slate-200 p-6">
                            <h3 className="text-lg font-bold text-slate-900 mb-4">Candidates by Simulation</h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={analytics.candidates_by_simulation} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                                    <YAxis type="category" dataKey="simulation_title" tick={{ fill: '#64748b', fontSize: 11 }} width={150} />
                                    <Tooltip />
                                    <Bar dataKey="count" fill={CHART_COLORS.secondary} radius={[0, 8, 8, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : null}

                    {/* Leaderboard and Recent Activity */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {analyticsLoading ? (
                            <>
                                <LeaderboardSkeleton />
                                <ActivitySkeleton />
                            </>
                        ) : analytics ? (
                            <>
                                {/* Top Performers Leaderboard */}
                                <div className="bg-white rounded-xl border border-slate-200 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-bold text-slate-900">Top Performers</h3>
                                        <Target className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    {analytics.top_performers && analytics.top_performers.length > 0 ? (
                                        <div className="space-y-3">
                                            {analytics.top_performers.map((performer, index) => (
                                                <div
                                                    key={performer.id}
                                                    className="flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors"
                                                >
                                                    <div className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                                                        {index + 1}
                                                    </div>
                                                    {performer.image_url ? (
                                                        <img
                                                            src={performer.image_url}
                                                            alt={performer.name}
                                                            className="w-10 h-10 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className={clsx(
                                                            'w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm',
                                                            getAvatarColor(performer.name)
                                                        )}>
                                                            {getInitials(performer.name)}
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-slate-900 truncate">{performer.name}</div>
                                                        <div className="text-xs text-slate-500 truncate">{performer.simulation_title}</div>
                                                    </div>
                                                    <div className={clsx('text-lg font-bold', getScoreColor(performer.score))}>
                                                        {performer.score}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="py-8 text-center text-slate-400">
                                            <Trophy className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                            <p>No completed evaluations yet</p>
                                        </div>
                                    )}
                                </div>

                                {/* Recent Activity */}
                                <div className="bg-white rounded-xl border border-slate-200 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-bold text-slate-900">Recent Activity</h3>
                                        <Activity className="w-5 h-5 text-blue-600" />
                                    </div>
                                    {analytics.recent_activity && analytics.recent_activity.length > 0 ? (
                                        <div className="space-y-3">
                                            {analytics.recent_activity.map((activity) => (
                                                <div
                                                    key={activity.id}
                                                    className="flex items-start gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors"
                                                >
                                                    {activity.image_url ? (
                                                        <img
                                                            src={activity.image_url}
                                                            alt={activity.name}
                                                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                                        />
                                                    ) : (
                                                        <div className={clsx(
                                                            'w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm flex-shrink-0',
                                                            getAvatarColor(activity.name)
                                                        )}>
                                                            {getInitials(activity.name)}
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-slate-900">{activity.name}</div>
                                                        <div className="text-xs text-slate-600 mt-1 truncate">
                                                            Completed {activity.simulation_title}
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-2">
                                                            <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium border', getStatusColor(activity.status))}>
                                                                {activity.status.replace('_', ' ')}
                                                            </span>
                                                            {activity.score !== null && (
                                                                <span className="text-xs text-slate-500">
                                                                    Score: <span className={clsx('font-semibold', getScoreColor(activity.score))}>{activity.score}</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-slate-400 flex-shrink-0">
                                                        {formatDate(activity.completed_at)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="py-8 text-center text-slate-400">
                                            <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                            <p>No recent activity</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : null}
                    </div>

                    {/* External Candidates (Invited) */}
                    {!invitedLoading && invitedCandidates.length > 0 && (
                        <div className="bg-white rounded-xl border border-slate-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900">External Candidates</h3>
                                    <p className="text-sm text-slate-500">Candidates invited via email</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    {invitedStats && (
                                        <div className="flex items-center gap-2 text-xs">
                                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">{invitedStats.scheduled} Scheduled</span>
                                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">{invitedStats.in_progress} In Progress</span>
                                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">{invitedStats.completed} Completed</span>
                                        </div>
                                    )}
                                    <Mail className="w-5 h-5 text-blue-600" />
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-slate-200">
                                            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Candidate</th>
                                            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Simulation</th>
                                            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                                            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Invited</th>
                                            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Score</th>
                                            <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {invitedCandidates.slice(0, 10).map((candidate) => (
                                            <tr key={candidate.id} className="hover:bg-slate-50 transition-colors">
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={clsx(
                                                            'w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-xs',
                                                            getAvatarColor(candidate.email)
                                                        )}>
                                                            {getInitials(candidate.email)}
                                                        </div>
                                                        <div>
                                                            <div className="font-medium text-slate-900 text-sm">{candidate.email}</div>
                                                            {candidate.invited_by && (
                                                                <div className="text-xs text-slate-500">Invited by {candidate.invited_by}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="text-sm text-slate-700">{candidate.simulation_title}</div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className={clsx(
                                                        'px-2 py-1 rounded-full text-xs font-medium border',
                                                        getStatusColor(candidate.status)
                                                    )}>
                                                        {getStatusLabel(candidate.status)}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center gap-1 text-sm text-slate-500">
                                                        <Calendar className="w-3.5 h-3.5" />
                                                        {formatDate(candidate.invited_at)}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    {candidate.score !== null && candidate.score !== undefined ? (
                                                        <span className={clsx('font-semibold', getScoreColor(candidate.score))}>
                                                            {candidate.score}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400 text-sm">-</span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 text-right">
                                                    {candidate.status === 'completed' && candidate.session_id && (
                                                        <button
                                                            onClick={() => navigate(`/app/evaluation/${candidate.session_id}`)}
                                                            className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center gap-1 ml-auto"
                                                        >
                                                            View Results
                                                            <ExternalLink className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {invitedCandidates.length > 10 && (
                                <div className="mt-4 text-center">
                                    <button
                                        onClick={() => navigate('/app/results')}
                                        className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                                    >
                                        View all {invitedCandidates.length} invited candidates
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Status Summary Cards */}
                    {!analyticsLoading && analytics && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                                        <CheckCircle2 className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-emerald-900">{analytics.status_breakdown.completed}</div>
                                        <div className="text-sm text-emerald-700">Completed</div>
                                    </div>
                                </div>
                                <div className="text-xs text-emerald-600">
                                    Ready for review and hiring decisions
                                </div>
                            </div>

                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                                        <Clock className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-blue-900">{analytics.status_breakdown.in_progress}</div>
                                        <div className="text-sm text-blue-700">In Progress</div>
                                    </div>
                                </div>
                                <div className="text-xs text-blue-600">
                                    Currently being evaluated
                                </div>
                            </div>

                            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                                        <Target className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-purple-900">{analytics.status_breakdown.not_started}</div>
                                        <div className="text-sm text-purple-700">Not Started</div>
                                    </div>
                                </div>
                                <div className="text-xs text-purple-600">
                                    Awaiting simulation start
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
